import React, { useCallback, useRef, useState } from 'react';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  isConcreteShopId,
  resolveConcreteShopId,
} from '@/utils/platformShopScope';

/**
 * 全部店铺视图下写操作前选具体店（Promise + Modal）。
 * @param {{ shops?: array, initialShopId?: string }} [options]
 */
export function usePlatformShopPick({ shops = [], initialShopId = '' } = {}) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState({});
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  const pickShop = useCallback((opts = {}) => new Promise((resolve) => {
    const shopList = Array.isArray(opts.shops) ? opts.shops : shops;
    if (!Array.isArray(shopList) || shopList.length === 0) {
      resolve(null);
      return;
    }
    if (shopList.length === 1 && isConcreteShopId(shopList[0]?.id)) {
      resolve(String(shopList[0].id));
      return;
    }
    resolverRef.current = resolve;
    setConfig(opts && typeof opts === 'object' ? opts : {});
    setOpen(true);
  }), [shops]);

  const shopPickModal = (
    <PlatformShopPickModal
      open={open}
      shops={Array.isArray(config.shops) ? config.shops : shops}
      title={config.title || '选择店铺'}
      description={config.description || '全部视图下请选择一家店铺后再继续。'}
      confirmText={config.confirmText || '确认'}
      initialShopId={config.initialShopId || initialShopId || ''}
      onClose={() => close(null)}
      onConfirm={(shopId) => close(isConcreteShopId(shopId) ? String(shopId) : null)}
    />
  );

  return { pickShop, shopPickModal };
}

/**
 * 解析写操作目标店：页内已是具体店则直接用；全部视图则弹窗选择。
 * @returns {Promise<string|null>}
 */
export async function resolveWriteShopId({
  listAllShops = false,
  pageShopScope = '',
  listShopId = '',
  pickShop,
  shops,
  title = '选择店铺',
  description = '全部视图下请选择一家店铺后再继续。',
  confirmText = '确认',
} = {}) {
  const fromPage = resolveConcreteShopId(pageShopScope);
  if (fromPage) return fromPage;
  if (!listAllShops && isConcreteShopId(listShopId)) return String(listShopId);
  if (typeof pickShop !== 'function') return null;
  return pickShop({
    title,
    description,
    confirmText,
    initialShopId: listShopId || '',
    ...(Array.isArray(shops) ? { shops } : {}),
  });
}
